import {
  CmsContentAboutTeamSchema,
  type CmsContentAboutTeamForm,
} from "@/modules/cms/domain/contracts";
import { uploadImageToKey } from "@/shared/lib/upload-image-to-key";
import { ImageKeyUploadField } from "@/shared/ui/ImageKeyUploadField";
import {
  Button,
  FormCheckbox,
  FormInput,
  FormNumber,
  FormRoot,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { PlusIcon, TrashIcon } from "lucide-react";
import { useCallback, useState } from "react";
import type { Resolver, UseFormReturn } from "react-hook-form";

const IMAGE_KEY_PREFIX = "uploads/cms/about-team";

type AboutTeamFormProps = {
  content: CmsContentAboutTeamForm | null;
  onSubmit: (content: CmsContentAboutTeamForm) => void;
  onCancel: () => void;
  submitting?: boolean;
};

const defaultContent: CmsContentAboutTeamForm = {
  items: [],
  isActive: true,
};

export function AboutTeamForm({
  content,
  onSubmit,
  onCancel,
  submitting,
}: AboutTeamFormProps) {
  const methods = RHF.useForm<CmsContentAboutTeamForm>({
    resolver: zodResolver(
      CmsContentAboutTeamSchema,
    ) as Resolver<CmsContentAboutTeamForm>,
    defaultValues: content ?? defaultContent,
  });
  const { fields, append, remove } = RHF.useFieldArray({
    control: methods.control,
    name: "items",
  });
  const [pendingFiles, setPendingFiles] = useState<Record<string, File>>({});

  const setPending = useCallback((index: number, file: File | null) => {
    setPendingFiles((prev) => {
      const next = { ...prev };
      if (file) next[index] = file;
      else delete next[index];
      return next;
    });
  }, []);

  const handleSubmit = useCallback(
    async (vals: CmsContentAboutTeamForm) => {
      const items = (vals.items ?? []).map((item, i) => ({
        imageKey: item.imageKey ?? "",
        name: item.name ?? {},
        position: item.position ?? {},
        isActive: item.isActive ?? true,
        order: item.order ?? i,
      }));
      for (let i = 0; i < items.length; i++) {
        const f = pendingFiles[i];
        if (f) {
          items[i].imageKey = await uploadImageToKey(IMAGE_KEY_PREFIX, f);
        }
      }
      setPendingFiles({});
      onSubmit({ ...vals, items });
    },
    [onSubmit, pendingFiles],
  );

  return (
    <FormRoot<CmsContentAboutTeamForm>
      methods={methods as UseFormReturn<CmsContentAboutTeamForm>}
      onSubmit={handleSubmit}
    >
      <div className="space-y-6">
        <div className="space-y-2">
          <span className="font-medium text-sm">ສະມາຊິກທີມງານ</span>
          {fields.map((field, index) => (
            <div
              key={field.id}
              className="rounded border p-4 md:grid md:grid-cols-[minmax(0,180px)_1fr] md:gap-4"
            >
              <div className="mb-3 md:mb-0">
                <ImageKeyUploadField
                  keyPrefix={IMAGE_KEY_PREFIX}
                  label="ຮູບ"
                  aspectHint="ອັດສ່ວນ 1:1"
                  aspectRatio="aspect-square"
                  widthPx={400}
                  heightPx={400}
                  className="max-w-[180px]"
                  value={methods.watch(`items.${index}.imageKey`) ?? ""}
                  onChange={(key) =>
                    methods.setValue(`items.${index}.imageKey`, key, {
                      shouldDirty: true,
                    })
                  }
                  deferUpload
                  pendingFile={pendingFiles[index] ?? null}
                  onFileSelect={(file) => setPending(index, file)}
                  onClear={() => {
                    methods.setValue(`items.${index}.imageKey`, "", {
                      shouldDirty: true,
                    });
                    setPending(index, null);
                  }}
                />
              </div>
              <div className="flex flex-col gap-3">
                <div className="flex flex-wrap items-end gap-3">
                  <FormNumber
                    name={`items.${index}.order`}
                    label="ລຳດັບ"
                    min={0}
                    className="w-24"
                  />
                  <FormCheckbox
                    name={`items.${index}.isActive`}
                    label="ເປີດໃຊ້ງານ"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => remove(index)}
                    aria-label="ລຶບ"
                    className="shrink-0"
                  >
                    <TrashIcon className="size-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                  <FormInput name={`items.${index}.name.lo`} label="ຊື່ (ລາວ)" />
                  <FormInput name={`items.${index}.name.th`} label="ຊື່ (ໄທ)" />
                  <FormInput name={`items.${index}.name.en`} label="ຊື່ (EN)" />
                </div>
                <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                  <FormInput
                    name={`items.${index}.position.lo`}
                    label="ຕຳແຫນ່ງ (ລາວ)"
                  />
                  <FormInput
                    name={`items.${index}.position.th`}
                    label="ຕຳແຫນ່ງ (ໄທ)"
                  />
                  <FormInput
                    name={`items.${index}.position.en`}
                    label="ຕຳແຫນ່ງ (EN)"
                  />
                </div>
              </div>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              append({
                imageKey: "",
                name: {},
                position: {},
                isActive: true,
                order: fields.length,
              })
            }
          >
            <PlusIcon className="size-4" />
            ເພີ່ມສະມາຊິກ
          </Button>
        </div>
        <FormCheckbox name="isActive" label="ເປີດໃຊ້ງານ" />
        <div className="flex gap-2 pt-2">
          <Button type="submit" disabled={submitting}>
            {submitting ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>
            ຍົກເລີກ
          </Button>
        </div>
      </div>
    </FormRoot>
  );
}
